import { supabase } from './supabase';
import type { NotificationEvent } from '../types';

export interface CreateNotificationArgs {
  userId: string;
  event: NotificationEvent | string;
  title: string;
  message: string;
  link?: string | null;
}

/**
 * Create an in-app notification for a user, then fire the email channel
 * fan-out (fire-and-forget). The email step is best-effort — failures are
 * logged but never propagate to the caller, so a flaky email path never
 * blocks the in-app notification or the originating workflow.
 */
export async function createNotification({
  userId,
  event,
  title,
  message,
  link = null,
}: CreateNotificationArgs): Promise<void> {
  type LooseRpc = (
    name: string,
    args: Record<string, unknown>
  ) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
  const callRpc = supabase.rpc.bind(supabase) as unknown as LooseRpc;
  const { error } = await callRpc('create_notification', {
    p_user_id: userId,
    p_event: event,
    p_title: title,
    p_message: message,
    p_link: link,
  });
  if (error) {
    console.error('[Arbi] createNotification error:', error.message);
    return;
  }
  fireEmail({ userId, event, title, message, link });
}

function fireEmail({ userId, event, title, message, link = null }: CreateNotificationArgs): void {
  supabase.functions
    .invoke('send-notification-email', {
      body: { user_id: userId, event, title, message, link },
    })
    .then(
      ({ error }) => {
        if (error) console.error('[Arbi] send-notification-email error:', error.message);
      },
      (e: unknown) => console.error('[Arbi] send-notification-email exception:', e)
    );
}

export interface NotificationEventGroup {
  label: string;
  events: string[];
}

/** Human-readable labels for notification events */
export const EVENT_LABELS: Record<string, string> = {
  po_submitted: 'PO Submitted',
  po_approved: 'PO Approved',
  po_edit_required: 'Edit Required Initiated',
  po_rejected: 'PO Rejected',
  po_ordered: 'PO Ordered',
  po_invoice_assigned: 'Invoice Assigned',
  po_processing_complete: 'Processing Complete',
  po_received: 'PO Received',
  po_discrepancy_flagged: 'Discrepancy Flagged',
  po_cancelled: 'PO Cancelled',
  po_chain_cancelled: 'Chain Cancelled',
  po_sent_to_distributor: 'PO Sent to Distributor',
  receiving_discrepancy: 'Receiving Discrepancy',
  file_uploaded: 'File Uploaded',
  task_assigned: 'Task Assigned',
  task_unassigned: 'Task Unassigned',
  task_overdue: 'Task Overdue',
  low_credit_warning: 'Low Credit Warning',
  system_alert: 'System Alert',
  feedback_submitted: 'Feedback Submitted',
};

/** Event groups for Settings UI */
export const EVENT_GROUPS: NotificationEventGroup[] = [
  {
    label: 'Purchase Orders',
    events: [
      'po_submitted',
      'po_approved',
      'po_edit_required',
      'po_rejected',
      'po_ordered',
      'po_invoice_assigned',
      'po_processing_complete',
      'po_received',
      'po_discrepancy_flagged',
      'po_cancelled',
      'po_chain_cancelled',
      // po_sent_to_distributor: dormant — wires Day 57 with the PO email action.
      'po_sent_to_distributor',
    ],
  },
  {
    label: 'Operations',
    events: ['receiving_discrepancy', 'file_uploaded'],
  },
  {
    label: 'Tasks',
    events: ['task_assigned', 'task_unassigned', 'task_overdue'],
  },
  {
    label: 'Financial',
    events: ['low_credit_warning'],
  },
  {
    label: 'System',
    events: ['system_alert', 'feedback_submitted'],
  },
];

/**
 * Fan-out helper: notify the PO/file submitter (if known) plus every active
 * admin, deduplicated. Optional `excludeUserId` skips the actor when that
 * matches an admin. Errors per-recipient are absorbed inside createNotification's
 * own console.error path; the helper itself never throws.
 */
export interface NotifySubmitterAndAdminsArgs {
  submitterUserId: string | null | undefined;
  event: NotificationEvent | string;
  title: string;
  message: string;
  link?: string | null;
  excludeUserId?: string | null;
}

/**
 * Fire low_credit_warning to admins when a distributor's available credit
 * falls below the configured danger threshold. Idempotent within a 24h window
 * keyed on the deep-link's distributor query param — so repeated PO approvals
 * against the same already-tight distributor don't spam admin inboxes.
 *
 * Called after every credit_ledger write that increases utilization
 * (soft_reserve, hard_draw). The helper itself fetches the latest credit
 * snapshot via get_credit_summary, reads credit_available_danger_pct from
 * params (business scope), and either fires or returns silently.
 *
 * Caller passes the distributor's display name so the title doesn't require a
 * separate distributor-row fetch.
 */
export async function maybeFireLowCreditWarning(
  distributorId: string,
  distributorName: string
): Promise<void> {
  const link = `/finance/credit-cash?distributor=${distributorId}`;

  // Snapshot the credit position via the same RPC FinanceCreditCash uses.
  type LooseRpc = (
    name: string,
    args: Record<string, unknown>
  ) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
  const callRpc = supabase.rpc.bind(supabase) as unknown as LooseRpc;
  const { data: summary, error: summaryError } = await callRpc('get_credit_summary', {
    p_distributor_id: distributorId,
  });
  if (summaryError) {
    console.error('[Arbi] maybeFireLowCreditWarning summary error:', summaryError.message);
    return;
  }
  const s = summary as {
    credit_limit?: number;
    available?: number;
    utilization_pct?: number;
  } | null;
  const limit = Number(s?.credit_limit ?? 0);
  const available = Number(s?.available ?? 0);
  if (limit <= 0) return;

  const { data: thresholdRow } = await supabase
    .from('params')
    .select('value')
    .eq('scope', 'business')
    .eq('key', 'credit_available_danger_pct')
    .maybeSingle();
  const dangerPct = Number((thresholdRow as { value?: number | string } | null)?.value ?? 20);
  const availablePct = (available / limit) * 100;
  if (availablePct >= dangerPct) return;

  const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const { data: priors } = await supabase
    .from('notifications')
    .select('id')
    .eq('event', 'low_credit_warning')
    .eq('link', link)
    .gte('created_at', since)
    .limit(1);
  if ((priors ?? []).length > 0) return;

  const availableUsd = available.toFixed(2);
  const availablePctRounded = availablePct.toFixed(1);

  const { data: admins } = await supabase
    .from('profiles')
    .select('id')
    .eq('role', 'admin')
    .eq('is_active', true);
  const targets = ((admins ?? []) as { id: string }[]).map((r) => r.id);
  await Promise.all(
    targets.map((userId) =>
      createNotification({
        userId,
        event: 'low_credit_warning',
        title: `${distributorName}: low credit available`,
        message: `Available credit at ${availablePctRounded}% of limit ($${availableUsd} remaining).`,
        link,
      })
    )
  );
}

export async function notifySubmitterAndAdmins(args: NotifySubmitterAndAdminsArgs): Promise<void> {
  const recipients = new Set<string>();
  if (args.submitterUserId) recipients.add(args.submitterUserId);

  const { data, error } = await supabase
    .from('profiles')
    .select('id')
    .eq('role', 'admin')
    .eq('is_active', true);
  if (error) {
    console.error('[Arbi] notifySubmitterAndAdmins fetch error:', error.message);
  } else {
    for (const row of (data ?? []) as { id: string }[]) recipients.add(row.id);
  }

  if (args.excludeUserId) recipients.delete(args.excludeUserId);

  await Promise.all(
    Array.from(recipients).map((userId) =>
      createNotification({
        userId,
        event: args.event,
        title: args.title,
        message: args.message,
        link: args.link ?? null,
      })
    )
  );
}
